import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiUsers, FiMail, FiPhone } from 'react-icons/fi';
import clientsService from '../../../../services/clients.service';
import { useAuth } from '../../../../context/AuthContext';
import SectionHelpModal from '../../../../components/common/SectionHelpModal';
import AppTour from '../../../../components/common/AppTour';
import Pagination from '../../../../components/common/Pagination';

const ClientList = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState('');
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'clientes') {
            setRunTour(true);
        }
        // fetchClients se lanzará a través del efecto de búsqueda/página
    }, [location.search]);

    // Efecto para buscar y paginar
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchClients(currentPage, searchTerm);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [currentPage, searchTerm]);

    const fetchClients = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const data = await clientsService.getAll({ page, search });
            
            if (data?.results) {
                setClients(data.results);
                setTotalCount(data.count || 0);
            } else if (Array.isArray(data)) {
                setClients(data);
                setTotalCount(data.length);
            } else {
                setClients([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error('Error fetching clients:', err);
            setError('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
            try {
                await clientsService.delete(id);
                setClients(clients.filter(c => c.id !== id));
            } catch (err) {
                console.error('Error deleting client:', err);
                alert('No se pudo eliminar el cliente. Puede que tenga cotizaciones asociadas.');
            }
        }
    };

    // Ya no necesitamos filteredClients local
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const clientesTourSteps = [
        {
            element: '#tour-clientes-header',
            intro: '<strong>Directorio de Clientes</strong>Aquí guardarás la información de las personas o empresas a las que les vendes. Tenerlos registrados te ahorrará mucho tiempo al cotizar.',
            position: 'bottom',
        },
        {
            element: '.dashboard-stats',
            intro: '<strong>Tipo de Clientes</strong>Este gráfico te muestra rápidamente cuántos de tus clientes son empresas y cuántos son personas naturales.',
            position: 'bottom',
        },
        {
            element: '.header-search',
            intro: '<strong>Buscador</strong>Encuentra a cualquier cliente en segundos escribiendo su nombre o su RUT.',
            position: 'bottom',
        },
        {
            element: '#tour-clientes-table',
            intro: '<strong>Datos de Contacto</strong>Aquí tendrás siempre a mano el correo y teléfono de cada cliente para contactarlo fácilmente.',
            position: 'top',
        },
        {
            element: '#tour-clientes-nuevo',
            intro: '<strong>Nuevo Cliente</strong>Aquí registrarás a tus contactos para armarles propuestas. Por ahora obsérvalo. ¡Vamos a la sección de Cotizaciones!',
            position: 'left',
        }
    ];

    if (loading && clients.length === 0) return <div className="loading-overlay"><div className="spinner"></div></div>;

    return (
        <div className="container-custom">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={clientesTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Cotizaciones' }}
                    onTourComplete={() => {
                        window.location.href = '/app/cotizaplus/cotizaciones?tour=cotizaciones';
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-clientes-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Gestión de Clientes</h2>
                        <SectionHelpModal
                            title="¿Cómo funciona tu Cartera de Clientes?"
                            content={
                                <div>
                                    <p>Esta es tu agenda digital de contactos. Aquí se guarda la información de todas las personas o empresas a las que les cotizas.</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Registro Único:</strong> Te recomendamos registrar a los clientes aquí primero. Cuando vayas a crear una nueva cotización, el sistema te dejará buscarlos rápidamente para no volver a escribir sus datos.</li>
                                        <li><strong>Datos Oficiales:</strong> Asegúrate de escribir bien el Nombre, RUT y Correo, ya que esa es la información exacta que aparecerá impresa en los PDFs y adonde se enviarán los correos de las propuestas.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Administra tu base de datos de clientes</p>
                </div>
                <button id="tour-clientes-nuevo" className="btn-custom btn-primary d-none d-lg-flex" onClick={() => navigate('/app/cotizaplus/clientes/nuevo')}>
                    <FiPlus size={20} /> Nuevo Cliente
                </button>
            </div>

            {error && <div className="alert-custom alert-danger">{error}</div>}

            <div className="card-custom">
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                    <div className="header-search" style={{ flex: 1 }}>
                        <FiSearch size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, email o RUT..."
                            className="header-search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                {clients.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <FiUsers size={48} color="var(--gray-400)" />
                        <p className="text-muted" style={{ marginTop: '10px' }}>No se encontraron clientes</p>
                    </div>
                ) : (
                    <>
                        {/* Vista Desktop: Tabla Clásica */}
                        <div className="table-responsive d-none d-lg-block" id="tour-clientes-table" style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>RUT</th>
                                        <th>Contacto</th>
                                        <th>Dirección</th>
                                        <th>Fecha Registro</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.map((client) => (
                                        <tr key={client.id}>
                                            <td>
                                                <div style={{ fontWeight: '500' }}>{client.nombre?.trim() || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>(Sin Nombre)</span>}</div>
                                            </td>
                                            <td>{client.rut_formateado || client.rut || '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    {client.email && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px' }}>
                                                            <FiMail size={12} color="var(--gray-600)" /> {client.email}
                                                        </div>
                                                    )}
                                                    {client.telefono && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px' }}>
                                                            <FiPhone size={12} color="var(--gray-600)" /> {client.telefono}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td><small>{client.direccion || '-'}</small></td>
                                            <td>{client.fecha_registro ? new Date(client.fecha_registro).toLocaleDateString('es-CL') : '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={() => navigate(`/app/cotizaplus/clientes/editar/${client.id}`)}
                                                        title="Editar"
                                                        style={{ backgroundColor: '#fff', color: 'var(--primary-orange)', border: '1px solid var(--primary-orange)' }}
                                                    >
                                                        <FiEdit2 size={16} /> Editar
                                                    </button>
                                                    {user?.rol === 'ADMIN' && (
                                                        <button
                                                            type="button"
                                                            className="btn-custom btn-sm"
                                                            onClick={() => handleDelete(client.id)}
                                                            title="Eliminar"
                                                            style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)' }}
                                                        >
                                                            <FiTrash2 size={16} /> Eliminar
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Vista Móvil: Tarjetas Apiladas */}
                        <div className="d-block d-lg-none mt-3">
                            {clients.map((client) => (
                                <div key={client.id} className="mobile-card">
                                    <div className="mobile-card-header">
                                        <span className="mobile-card-title" style={{ marginBottom: 0 }}>
                                            {client.nombre?.trim() || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>(Sin Nombre)</span>}
                                        </span>
                                    </div>
                                    <div className="mobile-card-body">
                                        <div className="mobile-card-subtitle" style={{ marginBottom: '8px' }}>
                                            <strong style={{ color: 'var(--text-dark)' }}>RUT:</strong> {client.rut_formateado || client.rut || '-'}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                                            {client.email && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--gray-600)' }}>
                                                    <FiMail size={12} /> {client.email}
                                                </div>
                                            )}
                                            {client.telefono && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--gray-600)' }}>
                                                    <FiPhone size={12} /> {client.telefono}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mobile-card-subtitle">
                                            <strong style={{ color: 'var(--text-dark)' }}>Dir:</strong> {client.direccion || '-'}
                                        </div>
                                    </div>
                                    <div className="mobile-card-footer">
                                        <div className="mobile-card-subtitle" style={{ fontSize: '12px' }}>
                                            Reg: {client.fecha_registro ? new Date(client.fecha_registro).toLocaleDateString('es-CL') : '-'}
                                        </div>
                                        <div className="mobile-card-actions">
                                            <button
                                                type="button"
                                                className="btn-custom btn-sm"
                                                onClick={() => navigate(`/app/cotizaplus/clientes/editar/${client.id}`)}
                                                style={{ backgroundColor: '#fff', color: 'var(--primary-orange)', border: '1px solid var(--primary-orange)' }}
                                            >
                                                <FiEdit2 size={14} /> Editar
                                            </button>
                                            {user?.rol === 'ADMIN' && (
                                                <button
                                                    type="button"
                                                    className="btn-custom btn-sm"
                                                    onClick={() => handleDelete(client.id)}
                                                    style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)' }}
                                                >
                                                    <FiTrash2 size={14} /> Eliminar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                )}
            </div>

            {/* Botón Flotante para Móviles */}
            <button
                className="fab-custom d-lg-none"
                onClick={() => navigate('/app/cotizaplus/clientes/nuevo')}
                title="Nuevo Cliente"
            >
                <FiPlus size={28} />
            </button>
        </div>
    );
};

export default ClientList;
